import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Stepper from '@material-ui/core/Stepper';
import Step from '@material-ui/core/Step';
import StepLabel from '@material-ui/core/StepLabel';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Check from '@material-ui/icons/Check';

import { NotificationEmail, NotificationSms } from './UnifiedNotifications';
import { dateTimeFormat, trackFullStoryEvent } from '../../helper';

const useStyles = makeStyles(() => ({
  root: {
    '& .MuiStepIcon-text': {
      opacity: 0,
    },
  },
  columnSeparator: {
    borderLeft: '1px solid #e8e8e8',
  },
  serviceList: {
    color: '#888',
    marginBottom: 5,
  },
  listLabel: {
    fontWeight: '600',
    color: '#000',
  },
  iconTick: {
    fontSize: 16,
    color: '#fff',
  },
  circle: {
    width: 25,
    height: 25,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '2px solid #d6d6d6',
    background: '#53AD32',
    zIndex: 9,
  },
  tabRoot: {
    '& .MuiTab-root': {
      minWidth: 100,
      border: '1px solid rgba(145, 158, 171, 0.24)',
      minHeight: 38,
      '&:first-child': {
        borderTopLeftRadius: 7,
        borderBottomLeftRadius: 7,
      },
      '&:last-child': {
        borderTopRightRadius: 7,
        borderBottomRightRadius: 7,
      },
      '&$selected': {
        borderColor: '#006C9C',
        backgroundColor: 'rgba(0, 184, 217, 0.08)',
        color: '#006C9C',
      },
    },
    '& .MuiTabs-indicator': {
      display: 'none',
    },
  },
  selected: {},
  mainBox: {
    marginBottom: 20,
    marginTop: 9,
    '& h5': {
      fontWeight: 700,
    },
  },
}));

const UnifiedCommunications = (record) => {
  const classes = useStyles();
  const [value, setValue] = useState(0);
  const handleChange = (event, newValue) => {
    trackFullStoryEvent('CTA_Communications_Tab_Switch', {
      tab: newValue === 0 ? 'Text' : 'Email',
      appointment_id: record?.id,
    });
    setValue(newValue);
  };

  return (
    <>
      <Grid item md={12} className={classes.mainBox}>
        <Typography variant="h5" gutterBottom>
          Communications
        </Typography>
      </Grid>
      <Box display="flex" flexWrap="wrap">
        <Box flex={1} pr="10px">
          <Tabs
            value={value}
            indicatorColor="primary"
            textColor="primary"
            onChange={handleChange}
            aria-label="communication"
            className={classes.tabRoot}
            left
          >
            <Tab label="Text" classes={{ selected: classes.selected }} />
            <Tab label="Email" classes={{ selected: classes.selected }} />
          </Tabs>
          <Box mt={2}>
            <TabPanel value={value} index={0}>
              <NotificationSms record={record} />
            </TabPanel>
            <TabPanel value={value} index={1}>
              <NotificationEmail record={record} />
            </TabPanel>
          </Box>
        </Box>
        <Box display="flex" flex={1} className={classes.columnSeparator}>
          <HistoryTab record={record} />
        </Box>
      </Box>
    </>
  );
};

export default UnifiedCommunications;

const TabPanel = ({ children, value, index }) => {
  return <>{value === index && children}</>;
};

const HistoryTab = ({ record }) => {
  const {
    booking_confirmation_email_sent_at,
    day_before_email_sent_at,
    day_before_sms_sent_at,
    on_our_way_sms_sent_at,
    service_complete_sms_email_sent_at,
    mark_complete_st_order_error,
    mark_complete_st_order_error_at,
  } = record;
  const classes = useStyles();
  function StepIcon() {
    return (
      <Box className={classes.circle}>
        <Check className={classes.iconTick} />
      </Box>
    );
  }
  return (
    <Box width="100%">
      <Typography variant="subtitle2" align="center">
        Timeline
      </Typography>

      <Stepper orientation="vertical" activeStep={-1} className={classes.root}>
        {service_complete_sms_email_sent_at && (
          <Step>
            <StepLabel StepIconComponent={StepIcon}>
              <Box display="flex" alignItems="center">
                <Typography variant="body2">
                  {`Service Complete Email:
                  ${dateTimeFormat(service_complete_sms_email_sent_at)}`}
                </Typography>
              </Box>
            </StepLabel>
          </Step>
        )}
        {mark_complete_st_order_error_at && (
          <Step>
            <StepLabel StepIconComponent={StepIcon}>
              <Box display="flex" alignItems="center">
                <Typography variant="body2">
                  {`Payment cannot be processed - ${dateTimeFormat(
                    mark_complete_st_order_error_at,
                  )}`}
                </Typography>
              </Box>
            </StepLabel>
          </Step>
        )}
        {day_before_email_sent_at && (
          <Step>
            <StepLabel StepIconComponent={StepIcon}>
              <Box display="flex" alignItems="center">
                <Typography variant="body2">
                  {`Day Before Reminder Email: 
                  ${dateTimeFormat(day_before_email_sent_at)}`}
                </Typography>
              </Box>
            </StepLabel>
          </Step>
        )}
        {day_before_sms_sent_at && (
          <Step>
            <StepLabel StepIconComponent={StepIcon}>
              <Box display="flex" alignItems="center">
                <Typography variant="body2">
                  {`Day Before Reminder SMS: 
                  ${dateTimeFormat(day_before_sms_sent_at)}`}
                </Typography>
              </Box>
            </StepLabel>
          </Step>
        )}
        {on_our_way_sms_sent_at && (
          <Step>
            <StepLabel StepIconComponent={StepIcon}>
              <Box display="flex" alignItems="center">
                <Typography variant="body2">
                  {`“On Our Way” SMS:
                  ${dateTimeFormat(on_our_way_sms_sent_at)}`}
                </Typography>
              </Box>
            </StepLabel>
          </Step>
        )}
        {booking_confirmation_email_sent_at && (
          <Step>
            <StepLabel StepIconComponent={StepIcon}>
              <Box display="flex" alignItems="center">
                <Typography variant="body2">
                  {`Booking Confirmation Email: 
                  ${dateTimeFormat(booking_confirmation_email_sent_at)}`}
                </Typography>
              </Box>
            </StepLabel>
          </Step>
        )}
      </Stepper>
    </Box>
  );
};
